import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTrackFiltersApplied, useTrackFiltersRemoved } from "@air/analytics";
import { Filters } from "@air/next-icons";
import { tailwindMerge } from "@air/tailwind-variants";
import { memo, useCallback } from "react";
import { useSelector } from "react-redux";
import { ColorFilterTrigger } from "~/components/Filters/FiltersBar/ColorFilterTrigger";
import { CreatedDateFilterTrigger } from "~/components/Filters/FiltersBar/CreatedDateFilterTrigger";
import { CustomFieldFilterTrigger } from "~/components/Filters/FiltersBar/CustomFieldFilterTrigger";
import { ModifiedDateFilterTrigger } from "~/components/Filters/FiltersBar/ModifiedDateFilterTrigger";
import { OrientationFilterTrigger } from "~/components/Filters/FiltersBar/OrientationFilterTrigger";
import { OtherFiltersTrigger } from "~/components/Filters/FiltersBar/OtherFiltersTrigger";
import { SmartTagFilterTrigger } from "~/components/Filters/FiltersBar/SmartTagFilterTrigger";
import { TagFilterTrigger } from "~/components/Filters/FiltersBar/TagFilterTrigger";
import { TypeFilterTrigger } from "~/components/Filters/FiltersBar/TypeFilterTrigger";
import { UploadedDateFilterTrigger } from "~/components/Filters/FiltersBar/UploadedDateFilterTrigger";
import { UploaderFilterTrigger } from "~/components/Filters/FiltersBar/UploaderFilterTrigger";
import { defaultOtherFilters } from "~/components/Filters/OtherFilters/OtherFilters";
import { PublicMoreFiltersTrigger } from "~/components/Filters/v2/components/PublicMoreFiltersTrigger";
import { usePublicFastFilterOptions } from "~/components/Filters/v2/hooks/usePublicFastFilterOptions";
import { manuallyHideDropdown } from "~/components/Menus/DropdownMenuWithActionSheet";
import { usePublicPermissions } from "~/components/PublicPermissionsProvider";
import { useApplyFilters } from "~/hooks/filters/useApplyFilters";
import { centralizedBoardSelector } from "~/store/centralizedBoard/selectors";
import { filtersValuesSelector } from "~/store/filters/selectors";
import { filterTypeToTrackingType } from "~/store/filters/utils";
import { useGetPublicFilterOptions } from "~/swr-hooks/filters/useGetPublicFilterOptions";
import { filterParamsToTrackingType, getUrlParamsFromFilters } from "~/utils/filters/filtersToUrlParamsUtils";
import { canSeeCustomFields, canSeeWorkspaceSmartTags, canSeeWorkspaceTags } from "~/utils/permissions/workspacePermissions";
import { useAirStore } from "~/utils/ReduxUtils";
const availableFilterTypes = [
    "assets",
    "files",
    "boards"
];
export const PublicFiltersBar = /*#__PURE__*/ memo((param)=>{
    let { className, trackLocation } = param;
    const { filtersInBar, filtersInDropdown, customFieldsInDropdown, otherFilters = defaultOtherFilters, disabledFilters } = usePublicFastFilterOptions();
    const board = useSelector(centralizedBoardSelector);
    var _board_id;
    const { data: filtersOptions } = useGetPublicFilterOptions({
        boardId: (_board_id = board === null || board === void 0 ? void 0 : board.id) !== null && _board_id !== void 0 ? _board_id : ""
    });
    const store = useAirStore();
    const { applyFilters, removeFilters } = useApplyFilters();
    const { trackFiltersRemoved } = useTrackFiltersRemoved();
    const { trackFiltersApplied } = useTrackFiltersApplied();
    const { permissions } = usePublicPermissions();
    const canViewCustomFields = canSeeCustomFields(permissions);
    const canSeeTags = canSeeWorkspaceTags(permissions);
    const canSeeSmartTags = canSeeWorkspaceSmartTags(permissions);
    const renderFilterTrigger = useCallback((filter)=>{
        const onApplyClick = async ()=>{
            const filters = filtersValuesSelector(store.getState());
            await applyFilters({
                filters
            });
            manuallyHideDropdown();
            const appliedFilters = getUrlParamsFromFilters(filters);
            trackFiltersApplied({
                types: filterParamsToTrackingType(appliedFilters),
                location: trackLocation,
                builderType: "pinned-filters"
            });
        };
        const onRemoveClick = async (filters)=>{
            await removeFilters({
                filters
            });
            trackFiltersRemoved({
                types: filterTypeToTrackingType(filter.type),
                location: trackLocation,
                builderType: "pinned-filters"
            });
        };
        const commonProps = {
            onApplyClick,
            onRemoveClick
        };
        switch(filter.type){
            case "tags":
                return canSeeTags && /*#__PURE__*/ _jsx(TagFilterTrigger, {
                    ...commonProps
                }, filter.type);
            case "smartTags":
                return canSeeSmartTags && /*#__PURE__*/ _jsx(SmartTagFilterTrigger, {
                    ...commonProps
                }, filter.type);
            case "color":
                var _filtersOptions_colors;
                return /*#__PURE__*/ _jsx(ColorFilterTrigger, {
                    colors: (_filtersOptions_colors = filtersOptions === null || filtersOptions === void 0 ? void 0 : filtersOptions.colors) !== null && _filtersOptions_colors !== void 0 ? _filtersOptions_colors : [],
                    ...commonProps
                }, filter.type);
            case "customField":
                return filter.customField && canViewCustomFields ? /*#__PURE__*/ _jsx(CustomFieldFilterTrigger, {
                    customField: filter.customField,
                    ...commonProps
                }, filter.customField.id) : null;
            case "dateCreated":
                return /*#__PURE__*/ _jsx(CreatedDateFilterTrigger, {
                    ...commonProps
                }, filter.type);
            case "dateModified":
                return /*#__PURE__*/ _jsx(ModifiedDateFilterTrigger, {
                    ...commonProps
                }, filter.type);
            case "dateUploaded":
                return /*#__PURE__*/ _jsx(UploadedDateFilterTrigger, {
                    ...commonProps
                }, filter.type);
            case "other":
                return /*#__PURE__*/ _jsx(OtherFiltersTrigger, {
                    otherFilters: otherFilters,
                    ...commonProps
                }, filter.type);
            case "type":
                return /*#__PURE__*/ _jsx(TypeFilterTrigger, {
                    availableTypes: availableFilterTypes,
                    ...commonProps
                }, filter.type);
            case "uploader":
                var _filtersOptions_uploaders;
                return /*#__PURE__*/ _jsx(UploaderFilterTrigger, {
                    uploaders: (_filtersOptions_uploaders = filtersOptions === null || filtersOptions === void 0 ? void 0 : filtersOptions.uploaders) !== null && _filtersOptions_uploaders !== void 0 ? _filtersOptions_uploaders : [],
                    ...commonProps
                }, filter.type);
            case "orientation":
                return /*#__PURE__*/ _jsx(OrientationFilterTrigger, {
                    ...commonProps
                }, filter.type);
            case "separator1":
                return /*#__PURE__*/ _jsx("div", {
                    className: "h-6 w-px bg-grey-4",
                    "aria-hidden": "true"
                }, filter.type);
        }
    }, [
        store,
        applyFilters,
        trackFiltersApplied,
        trackLocation,
        removeFilters,
        trackFiltersRemoved,
        canSeeTags,
        canSeeSmartTags,
        filtersOptions === null || filtersOptions === void 0 ? void 0 : filtersOptions.colors,
        filtersOptions === null || filtersOptions === void 0 ? void 0 : filtersOptions.uploaders,
        canViewCustomFields,
        otherFilters
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: tailwindMerge("flex items-center gap-2 overflow-auto px-3 pb-3 pt-2", className),
        children: [
            /*#__PURE__*/ _jsx(Filters, {
                className: "size-4 shrink-0 text-grey-8"
            }),
            filtersInBar.map((filter)=>renderFilterTrigger(filter)),
            /*#__PURE__*/ _jsx(PublicMoreFiltersTrigger, {
                trackLocation: trackLocation,
                otherFilters: otherFilters,
                availableTypes: availableFilterTypes,
                filters: filtersInDropdown,
                customFields: customFieldsInDropdown,
                disabledFilters: disabledFilters
            })
        ]
    });
});
PublicFiltersBar.displayName = "PublicFiltersBar";
